// Environment (SM-749 / SM-755 / SM-758): label, scenario epoch, the
// injectable clock (real / frozen / offset, with a reason), and the HMAC
// clients — status, callback URLs, limits, rotation overlap. No secret is
// ever displayed: the API has no read path to one. Rotation takes the new
// secret (already created in the environment's secret manager) in a
// password field, clears it after sending, and needs "ROTATE <client>".
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Check, Field, Select, TextArea, TextInput } from "@/components/ui/Field";
import { KV, Mono, PageHeader, ReadOnlyHint, TableWrap } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { api } from "@/lib/api";
import { disablePhrase, rotatePhrase } from "@/lib/confirm";
import { endpointName, fmtTime, localToRfc3339, rfc3339ToLocal } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import { rotateSecretOk } from "@/lib/rotateSecret";
import { useCanMutate } from "@/lib/session";
import type { Client, ClockSetting } from "@/lib/types";

export const Route = createFileRoute("/_app/environment")({
  component: EnvironmentPage,
});

function EnvironmentPage() {
  const env = useQuery({ queryKey: keys.environment(), queryFn: fetchers.environment, refetchInterval: 5_000 });
  const clients = useQuery({ queryKey: keys.clients(), queryFn: fetchers.clients });
  return (
    <div className="space-y-5">
      <PageHeader title="Environment" subtitle="What this mock is, what time it thinks it is, and which HMAC clients may call it." />
      <ErrorBanner error={env.error} />
      {env.isLoading || !env.data ? (
        <Skeleton lines={5} />
      ) : (
        <div className="card">
          <KV
            items={[
              ["Banner", <b>{env.data.banner}</b>],
              ["Environment label", <Mono className="text-base font-semibold">{env.data.environment_label}</Mono>],
              ["Scenario epoch", <Mono className="text-base font-semibold">{env.data.epoch}</Mono>],
              ["Mock clock now", <span>{fmtTime(env.data.now)} <span className="mono text-2xs t-mute">{env.data.now}</span></span>],
              ["Clock mode", <ClockSummary c={env.data.clock} />],
              ["Fault kinds", (env.data.fault_kinds ?? []).length],
              ["Provider endpoints", <span className="mono text-xs">{(env.data.provider_paths ?? []).map(endpointName).join(", ")}</span>],
            ]}
          />
        </div>
      )}
      {env.data && <ClockForm current={env.data.clock} />}
      <SectionCard title="HMAC clients">
        <ErrorBanner error={clients.error} />
        <ClientsTable rows={clients.data ?? []} />
      </SectionCard>
      {(clients.data ?? []).map((c) => (
        <ClientAdmin key={c.client_id} client={c} />
      ))}
    </div>
  );
}

function ClockSummary({ c }: { c: ClockSetting }) {
  if (c.mode === "frozen") return <span><b>frozen</b> at {fmtTime(c.at)}</span>;
  if (c.mode === "offset") return <span><b>offset</b> {c.offset_seconds}s from wall clock</span>;
  return <b>real</b>;
}

function ClockForm({ current }: { current: ClockSetting }) {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const [mode, setMode] = useState(current.mode);
  const [at, setAt] = useState(rfc3339ToLocal(current.at) || rfc3339ToLocal(new Date().toISOString()));
  const [offset, setOffset] = useState(String(current.offset_seconds ?? 3600));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const reasonOk = reason.trim().length >= 3;
  const offsetN = Number(offset);
  const valid = reasonOk && (mode === "real" || (mode === "frozen" && !!localToRfc3339(at)) || (mode === "offset" && Number.isInteger(offsetN) && offsetN !== 0));

  return (
    <SectionCard title="Clock">
      {!canMutate && <ReadOnlyHint />}
      <p className="mb-3 text-xs text-t-3">
        Governs launch-code expiry, the ±300 s timestamp window, nonce retention, pending resolution and every stored
        time. Persisted, so it survives restarts.
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!valid) return;
          const body: Record<string, unknown> = { mode, reason: reason.trim() };
          if (mode === "frozen") body.at = localToRfc3339(at);
          if (mode === "offset") body.offset_seconds = offsetN;
          setBusy(true);
          setError(null);
          try {
            await api("/clock", { method: "PUT", body });
            setReason("");
            await qc.invalidateQueries({ queryKey: keys.environment() });
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset disabled={!canMutate || busy}>
          <div className="form-grid">
            <Field label="Mode">
              <Select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="real">real — wall clock</option>
                <option value="frozen">frozen — always one instant</option>
                <option value="offset">offset — wall clock + seconds</option>
              </Select>
            </Field>
            {mode === "frozen" && (
              <Field label="Frozen at (your local time)">
                <TextInput type="datetime-local" step="1" value={at} onChange={(e) => setAt(e.target.value)} />
              </Field>
            )}
            {mode === "offset" && (
              <Field label="Offset (seconds)" hint="Negative goes back in time.">
                <TextInput value={offset} onChange={(e) => setOffset(e.target.value)} inputMode="numeric" className="mono" />
              </Field>
            )}
            <Field label="Reason (required)" error={reason && !reasonOk ? "At least 3 characters." : null} className="span-all">
              <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </div>
          <ErrorBanner error={error} className="mt-3" />
          <div className="mt-4 flex justify-end">
            <Button type="submit" loading={busy} disabled={!valid}>
              Set clock
            </Button>
          </div>
        </fieldset>
      </form>
    </SectionCard>
  );
}

function ClientsTable({ rows }: { rows: Client[] }) {
  return (
    <TableWrap>
      <table className="tbl">
        <thead>
          <tr>
            <th>Client id</th>
            <th>Status</th>
            <th>Callback URLs</th>
            <th>Rate limit</th>
            <th>Secret</th>
            <th>Rotation overlap</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.client_id}>
              <td>
                <Mono className="font-semibold">{c.client_id}</Mono>
                {c.display_name && <div className="text-2xs t-mute">{c.display_name}</div>}
              </td>
              <td>
                <StatusBadge status={c.status} />
              </td>
              <td className="wrap">
                {(c.callback_urls ?? []).map((u) => (
                  <div key={u} className="mono text-xs break-all">
                    {u}
                  </div>
                ))}
              </td>
              <td className="mono">
                {c.rate_limit_rps} rps / burst {c.rate_limit_burst}
              </td>
              <td>{c.secret_set ? <span className="pill neutral">set · never displayed</span> : <span className="pill no">not set</span>}</td>
              <td>{c.rotation_overlap_until ? `previous secret verifies until ${fmtTime(c.rotation_overlap_until)}` : "none"}</td>
              <td>{fmtTime(c.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function ClientAdmin({ client }: { client: Client }) {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  // --- update
  const [status, setStatus] = useState(client.status);
  const [urls, setUrls] = useState((client.callback_urls ?? []).join("\n"));
  const [rps, setRps] = useState(String(client.rate_limit_rps));
  const [burst, setBurst] = useState(String(client.rate_limit_burst));
  const [updReason, setUpdReason] = useState("");
  const [updOpen, setUpdOpen] = useState(false);
  const [updError, setUpdError] = useState<unknown>(null);
  const [updBusy, setUpdBusy] = useState(false);
  // --- rotate
  const [secret, setSecret] = useState("");
  const [emergency, setEmergency] = useState(false);
  const [rotReason, setRotReason] = useState("");
  const [rotOpen, setRotOpen] = useState(false);
  const [rotDone, setRotDone] = useState<string | null>(null);

  const updBody = (confirm: string) => ({
    status,
    callback_urls: urls
      .split(/\s+/)
      .map((u) => u.trim())
      .filter(Boolean),
    rate_limit_rps: Number(rps) || 0,
    rate_limit_burst: Number(burst) || 0,
    reason: updReason.trim(),
    confirm,
  });
  const sendUpdate = async (confirm: string) => {
    await api(`/clients/${encodeURIComponent(client.client_id)}`, { method: "PUT", body: updBody(confirm) });
    setUpdReason("");
    await qc.invalidateQueries({ queryKey: keys.clients() });
  };
  const disabling = status === "DISABLED" && client.status !== "DISABLED";
  const secretOk = rotateSecretOk(secret);

  return (
    <SectionCard title={<span>Client <Mono>{client.client_id}</Mono></span>}>
      {!canMutate && <ReadOnlyHint />}
      <div className="grid gap-6 xl:grid-cols-2">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (updReason.trim().length < 3) return;
            if (disabling) {
              setUpdOpen(true);
              return;
            }
            setUpdBusy(true);
            setUpdError(null);
            try {
              await sendUpdate("");
            } catch (err) {
              setUpdError(err);
            } finally {
              setUpdBusy(false);
            }
          }}
        >
          <fieldset disabled={!canMutate || updBusy}>
            <div className="mb-2 text-sm font-semibold text-t-1">Status, callbacks and limits</div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Status">
                <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option>ACTIVE</option>
                  <option>DISABLED</option>
                </Select>
              </Field>
              <Field label="Rate (rps)">
                <TextInput value={rps} onChange={(e) => setRps(e.target.value)} inputMode="numeric" className="mono" />
              </Field>
              <Field label="Burst">
                <TextInput value={burst} onChange={(e) => setBurst(e.target.value)} inputMode="numeric" className="mono" />
              </Field>
            </div>
            <Field label="Registered callback URLs (one per line, exact match)" className="mt-3">
              <TextArea rows={3} value={urls} onChange={(e) => setUrls(e.target.value)} className="mono text-xs" />
            </Field>
            <Field label="Reason (required)" className="mt-3">
              <TextInput value={updReason} onChange={(e) => setUpdReason(e.target.value)} />
            </Field>
            <ErrorBanner error={updError} className="mt-3" />
            <div className="mt-3 flex justify-end">
              <Button type="submit" variant={disabling ? "danger" : "primary"} loading={updBusy} disabled={updReason.trim().length < 3}>
                Save client
              </Button>
            </div>
          </fieldset>
        </form>

        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (!secretOk || rotReason.trim().length < 3) return;
            setRotOpen(true);
          }}
        >
          <fieldset disabled={!canMutate}>
            <div className="mb-2 text-sm font-semibold text-t-1">Rotate HMAC secret</div>
            <div className="banner warn" role="note">
              <span aria-hidden>⚠</span>
              <span>
                Create the new secret in this environment's <b>secret manager first</b>, then paste it here. The console
                never shows, stores or returns a secret — after rotating, Smart Market's signer must read the new value from
                the secret manager.
              </span>
            </div>
            <Field label="New secret (base64)" hint="Any length the provider uses. Sent once, then cleared from this form.">
              <TextInput
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                className="mono"
                name={`rotate-${client.client_id}`}
              />
            </Field>
            <div className="mt-3">
              <Check
                label="Emergency revocation — no overlap"
                hint="Without it the previous secret keeps verifying for 7 days."
                checked={emergency}
                onChange={(e) => setEmergency(e.target.checked)}
              />
            </div>
            <Field label="Reason (required)" className="mt-3">
              <TextInput value={rotReason} onChange={(e) => setRotReason(e.target.value)} />
            </Field>
            {rotDone && (
              <div className="banner mt-3" role="status" style={{ borderColor: "var(--yes)" }}>
                <span aria-hidden>✓</span>
                <span>{rotDone}</span>
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <Button type="submit" variant="danger" disabled={!secretOk || rotReason.trim().length < 3}>
                Rotate secret
              </Button>
            </div>
          </fieldset>
        </form>
      </div>

      <ConfirmDialog
        open={updOpen}
        title={`Disable client ${client.client_id}?`}
        consequence={<>Every provider request signed by this client is refused after signature verification, until it is set ACTIVE again.</>}
        phrase={disablePhrase(client.client_id)}
        confirmLabel="Disable client"
        onConfirm={sendUpdate}
        onClose={() => setUpdOpen(false)}
      />
      <ConfirmDialog
        open={rotOpen}
        title={`Rotate the secret of ${client.client_id}?`}
        consequence={
          emergency ? (
            <>The current secret stops verifying <b>immediately</b>. Any signer still using it is refused 401 INVALID_SIGNATURE.</>
          ) : (
            <>The new secret verifies at once; the current one keeps verifying for <b>7 days</b> (planned rotation overlap).</>
          )
        }
        phrase={rotatePhrase(client.client_id)}
        confirmLabel="Rotate secret"
        onConfirm={async (confirm) => {
          await api(`/clients/${encodeURIComponent(client.client_id)}/rotate`, {
            method: "POST",
            body: { new_secret_base64: secret.trim(), emergency, reason: rotReason.trim(), confirm },
          });
          setSecret("");
          setRotReason("");
          setRotDone(emergency ? "Secret rotated with no overlap." : "Secret rotated; the previous one verifies for 7 more days.");
          await qc.invalidateQueries({ queryKey: keys.clients() });
        }}
        onClose={() => setRotOpen(false)}
      />
    </SectionCard>
  );
}
